import { cn } from '@/src/utils';
import { cva } from 'class-variance-authority';
import React, { forwardRef, HTMLAttributes, ReactNode } from 'react';
import Typo from '../../atom/Typo';

type ItemDirectionType = 'horizontal' | 'vertical';
type LabelSize = 'M' | 'L' | 'Full';

interface SectionLabelProps extends HTMLAttributes<HTMLDivElement> {
  value: string | ReactNode;
  label: string | ReactNode;
  labelSize?: LabelSize;
  labelProps?: HTMLAttributes<HTMLLabelElement>;
  direction?: ItemDirectionType;
  isRequired?: boolean;
}

const sectionLabelContainerVariants = cva('inline-flex', {
  variants: {
    direction: {
      horizontal: 'flex justify-start gap-2',
      vertical: 'flex w-full flex-col items-start gap-2',
    },
  },
  defaultVariants: {
    direction: 'horizontal',
  },
});

const sectionLabelVariants = cva('text-b16m text-gray9', {
  variants: {
    labelSize: {
      L: 'w-[208px] max-w-[208px]',
      M: 'w-[164px] max-w-[164px]',
      Full: 'w-full',
    },
  },
  defaultVariants: { labelSize: 'M' },
});

const SectionLabel = forwardRef<HTMLDivElement, SectionLabelProps>(
  (
    {
      label,
      direction = 'horizontal',
      labelSize = 'M',
      labelProps,
      value,
      isRequired = false,
      className,
      ...props
    },
    ref,
  ) => {
    const renderValue = () => {
      if (typeof value === 'string') {
        return (
          <Typo typoType="b16r" typoColor="gray6">
            {value}
          </Typo>
        );
      }

      return value;
    };

    return (
      <div
        ref={ref}
        className={cn(sectionLabelContainerVariants({ direction }), className)}
        {...props}
      >
        <Typo
          typoType="b16m"
          {...labelProps}
          className={cn(
            'flex-shrink-0',
            sectionLabelVariants({ labelSize }),
            labelProps?.className,
          )}
        >
          {label}
          {isRequired && <span className="ml-1 text-red5">*</span>}
        </Typo>
        {renderValue()}
      </div>
    );
  },
);

export default SectionLabel;
